import { IonInput } from '@ionic/angular';
import { Subscription } from 'rxjs';
import * as leaflet from 'leaflet';
import 'leaflet.markercluster';

import Address from 'src/smoothr-web-app-core/models/Address';
import { getLatLng, sleep } from 'src/smoothr-web-app-core/utils/utils';
import { Api } from 'src/smoothr-web-app-core/api/api';
import { ValidationUtils } from 'src/smoothr-web-app-core/utils/validation-utils';
import { NominatimPlace } from 'src/smoothr-web-app-core/models/NominatimPlace';
import Venue from 'src/smoothr-web-app-core/models/Venue';
import Order from 'src/smoothr-web-app-core/models/Order';

export const selectedMark = '/assets/demo/pin.svg';
export const notSelectedMark = '/assets/pin_local.svg';

export class MapsUtils {
	static initAutocomplete(
		ionInput: IonInput,
		callback: (predictions: Address[]) => void,
		loadingCallback: (loading: boolean) => void
	): Subscription {
		let prevRequest: Promise<Address[]> = null;
		return ionInput.ionChange.subscribe(async event => {
			if (prevRequest) {
				await prevRequest;
				await sleep(100);
			}
			loadingCallback(true);
			const value = event.detail.value;
			if (value.replace(' ', '').length === 0) {
				callback([]);
				loadingCallback(false);
				return;
			}
			prevRequest = new Promise<Address[]>(async (resolve, reject) => {
				try {
					resolve(await this.getNominatimPrediction(value));
				} catch (e) {
					reject(e);
				}
			});
			callback(await prevRequest);
			loadingCallback(false);
			prevRequest = null;
		});
	}

	static async getNominatimPrediction(input: string): Promise<Address[]> {
		const data = (await Api.mapSearch(input, 5)).data;
		console.log(
			data.map(nplace => ({
				title: nplace.display_name,
				importance: nplace.importance,
				category: nplace.category,
			}))
		);
		const addresses = data
			.map(nplace => this.placeToAddress(nplace))
			.filter(address => !ValidationUtils.validateAddress(address, false, false));
		const titles = addresses.map(address => this.addressToString(address));
		return addresses.filter((address, addressIndex) => {
			return titles.indexOf(titles[addressIndex]) === addressIndex;
		});
	}

	static checkAddress(address: Address): string {
		const addressError = ValidationUtils.validateAddress(address, false, false);
		if (addressError) {
			console.error('Maps error: ' + addressError);
			throw addressError;
		}
		return this.addressToString(address);
	}

	static async executeSearch(ionInput: IonInput): Promise<Address> {
		return new Promise<Address>(async (resolve, reject) => {
			const input = await ionInput.getInputElement();
			const inputValue = input.value;
			if (!inputValue || inputValue.length <= 0) {
				reject();
				return;
			}
			try {
				const result = MapsUtils.getPlace(inputValue);
				if (result) {
					resolve(result);
				} else {
					reject('No results');
				}
				return;
			} catch (e) {
				reject(e);
			}
		});
	}

	static async getPlace(pred: string): Promise<Address> {
		return new Promise<Address>(async (resolve, reject) => {
			try {
				const predictions = await this.getNominatimPrediction(pred);
				if (predictions.length < 1) {
					resolve(null);
					return;
				}
				resolve(predictions[0]);
			} catch (e) {
				reject(e);
			}
		});
	}

	static placeToAddress(place: NominatimPlace): Address {
		if (!place) {
			return null;
		}
		const address = new Address();
		address.number = place.address.house_number || place.address.house_name || null;
		address.street = place.address.road || place.address.construction || null;
		address.city = place.address.city || place.address.town || place.address.village || place.address.municipality || null;
		address.state =
			place.address.state ||
			place.address.region ||
			place.address.state_district ||
			place.address.county ||
			place.address.city ||
			null;
		address.country = place.address.country || place.address.country_code || null;
		address.postalCode = place.address.postcode || null;
		address.lat = +place.lat || null;
		address.lng = +place.lon || null;
		address.placeId = place.place_id;
		address.displayName = place.display_name;
		return address;
	}

	static addressToString(address: Address): string {
		if (!address) {
			return '';
		}
		if (address.displayName?.length > 0) {
			return address.displayName;
		}
		let title = '';
		if (address.street) {
			title += address.street;
		}
		if (address.number) {
			title += ' ' + address.number;
		}
		if (title !== '' && (address.postalCode || address.city)) {
			title += ',';
		}
		if (address.postalCode) {
			if (title !== '') {
				title += ' ';
			}
			title += address.postalCode;
		}
		if (address.city) {
			if (title !== '') {
				title += ' ';
			}
			title += address.city;
		}

		return title;
	}

	static async getUserGeocode(): Promise<Address> {
		const geocoderPromise = new Promise<Address>((resolve, reject) => {
			navigator.geolocation.getCurrentPosition(
				async position => {
					try {
						const result = (await Api.geocode(position.coords.latitude, position.coords.longitude)).data;
						console.log(result);
						if (result) {
							resolve(MapsUtils.placeToAddress(result));
						} else {
							reject('No result');
						}
					} catch (e) {
						reject(e);
					}
				},
				error => reject(error)
			);
		});
		const timeoutPromise = new Promise<Address>((resolve, reject) => {
			setTimeout(() => reject('Timeout'), 10000);
		});
		return Promise.race([geocoderPromise, timeoutPromise]);
	}

	static getClusterIcon(count: number) {
		const svg = `
		<svg width="43" height="47" viewBox="0 0 43 47" fill="none" xmlns="http://www.w3.org/2000/svg">
		<g filter="url(#filter0_d_48_2443)">
		<mask id="path-1-inside-1_48_2443" fill="white">
		<path fill-rule="evenodd" clip-rule="evenodd" d="M23.6447 34.8699C32.298 33.8125 39 26.4389 39 17.5C39 7.83502 31.165 0 21.5 0C11.835 0 4 7.83502 4 17.5C4 26.2753 10.4589 33.542 18.8821 34.8055L21.282 38.9622L23.6447 34.8699Z"/>
		</mask>
		<path fill-rule="evenodd" clip-rule="evenodd" d="M23.6447 34.8699C32.298 33.8125 39 26.4389 39 17.5C39 7.83502 31.165 0 21.5 0C11.835 0 4 7.83502 4 17.5C4 26.2753 10.4589 33.542 18.8821 34.8055L21.282 38.9622L23.6447 34.8699Z" fill="#0D47A1"/>
		<path d="M23.6447 34.8699L23.5234 33.8773L23.0281 33.9378L22.7787 34.3699L23.6447 34.8699ZM18.8821 34.8055L19.7481 34.3055L19.5071 33.8881L19.0305 33.8166L18.8821 34.8055ZM21.282 38.9622L20.416 39.4622L21.282 40.9622L22.148 39.4622L21.282 38.9622ZM38 17.5C38 25.9274 31.6812 32.8804 23.5234 33.8773L23.766 35.8625C32.9148 34.7445 40 26.9504 40 17.5H38ZM21.5 1C30.6127 1 38 8.3873 38 17.5H40C40 7.28273 31.7173 -1 21.5 -1V1ZM5 17.5C5 8.3873 12.3873 1 21.5 1V-1C11.2827 -1 3 7.28273 3 17.5H5ZM19.0305 33.8166C11.0895 32.6254 5 25.7729 5 17.5H3C3 26.7776 9.82837 34.4586 18.7338 35.7944L19.0305 33.8166ZM22.148 38.4622L19.7481 34.3055L18.0161 35.3055L20.416 39.4622L22.148 38.4622ZM22.7787 34.3699L20.416 38.4622L22.148 39.4622L24.5107 35.3699L22.7787 34.3699Z" fill="white" mask="url(#path-1-inside-1_48_2443)"/>
		<text x="21.5" y="20" text-anchor="middle" dominant-baseline="middle" font-size="16" font-family="Arial, Helvetica, sans-serif" fill="white">${count}</text>
		</g>
		<defs>
		<filter id="filter0_d_48_2443" x="0" y="0" width="43" height="46.9622" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
		<feFlood flood-opacity="0" result="BackgroundImageFix"/>
		<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
		<feOffset dy="4"/>
		<feGaussianBlur stdDeviation="2"/>
		<feComposite in2="hardAlpha" operator="out"/>
		<feColorMatrix type="matrix" values="0 0 0 0 0.309804 0 0 0 0 0.380392 0 0 0 0 0.309804 0 0 0 1 0"/>
		<feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_48_2443"/>
		<feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_48_2443" result="shape"/>
		</filter>
		</defs>
		</svg>
		`;
		const blob = new Blob([svg], { type: 'image/svg+xml' });
		const url = URL.createObjectURL(blob);

		return leaflet.icon({
			iconUrl: url,
			iconSize: [43, 47],
			iconAnchor: [21.5, 47],
			popupAnchor: [1, -34],
		});
	}

	static addVenuesToMap(
		clusterGroup: leaflet.MarkerClusterGroup,
		selectedVenue: Venue,
		venues: Venue[],
		map: leaflet.Map,
		onClick: (venue: Venue) => void
	): leaflet.MarkerClusterGroup {
		clusterGroup =
			clusterGroup ||
			leaflet.markerClusterGroup({
				chunkedLoading: true,
				maxClusterRadius: 30,
				zoomToBoundsOnClick: true,
				animateAddingMarkers: true,
				animate: true,
				iconCreateFunction: function (cluster: any) {
					return MapsUtils.getClusterIcon(cluster.getAllChildMarkers().length);
				},
			});

		// Clear existing markers
		clusterGroup.clearLayers();
		if (!map.hasLayer(clusterGroup)) {
			clusterGroup.addTo(map);
		}

		const newVenues = venues.filter(
			ven =>
				clusterGroup
					.getLayers()
					.flatMap((cluster: leaflet.MarkerCluster) => cluster.getAllChildMarkers())
					.find(
						mark =>
							ven.location?.coordinates[1] === mark.getLatLng().lat && ven.location?.coordinates[0] === mark.getLatLng().lng
					) === undefined
		);
		const marksToRemove = clusterGroup.getLayers().flatMap((cluster: leaflet.MarkerCluster) =>
			cluster
				.getAllChildMarkers()
				.map((mark, index) =>
					venues.find(
						ven =>
							ven.location?.coordinates[1] === mark.getLatLng().lat && ven.location?.coordinates[0] === mark.getLatLng().lng
					) === undefined
						? index
						: -1
				)
				.filter(i => i >= 0)
		);

		for (const markToRemove of marksToRemove.reverse()) {
			clusterGroup.removeLayer(clusterGroup.getLayers()[markToRemove]);
		}

		const bounds = leaflet.latLngBounds([]);
		const markers: leaflet.Marker[] = [];

		for (const venue of venues) {
			const latLng = getLatLng(venue);
			if (!latLng) {
				continue;
			}

			const position = leaflet.latLng(latLng.latitude, latLng.longitude);
			if (latLng.latitude !== 0 && latLng.longitude !== 0) {
				bounds.extend(position);
			}

			if (newVenues.find(ven => ven._id === venue._id)) {
				const marker = leaflet.marker(position, {
					icon: leaflet.icon({
						iconUrl: notSelectedMark,
						iconSize: [43, 47],
						iconAnchor: [21.5, 47],
						popupAnchor: [1, -34],
					}),
				});

				marker.on('click', () => {
					onClick(venue);
				});

				markers.push(marker);
			}
		}

		// Add all markers to the cluster group
		clusterGroup.addLayers(markers);

		// If there's a selected venue, highlight it and center the map
		if (selectedVenue && selectedVenue.location) {
			const selectedMarker = markers.find(marker => {
				const pos = marker.getLatLng();
				return selectedVenue.location.coordinates[1] === pos.lat && selectedVenue.location.coordinates[0] === pos.lng;
			});

			if (selectedMarker) {
				selectedMarker.setIcon(
					leaflet.icon({
						iconUrl: selectedMark,
						iconSize: [43 * 1.8, 47 * 1.8],
						iconAnchor: [21.5, 47],
						popupAnchor: [1, -34],
					})
				);

				const pos = selectedMarker.getLatLng();
				const selectedBounds = leaflet.latLngBounds(
					leaflet.latLng(pos.lat - 0.01, pos.lng - 0.01),
					leaflet.latLng(pos.lat + 0.01, pos.lng + 0.01)
				);
				map.fitBounds(selectedBounds);
				map.panTo(pos);
			}
		} else if (bounds.getNorthEast() && bounds.getSouthWest()) {
			map.fitBounds(bounds, {
				padding: [50, 50],
			});
		} else {
			map.setView(leaflet.latLng(51.1657, 10.4515), 6);
		}

		return clusterGroup;
	}

	static scrollToMap(clusterGroup: leaflet.MarkerClusterGroup, map: leaflet.Map, selectedVenue: Venue): leaflet.MarkerClusterGroup {
		if (selectedVenue && selectedVenue.location) {
			const markers = clusterGroup.getLayers() as leaflet.Marker[];
			const selectedMarker = markers.find(marker => {
				const pos = marker.getLatLng();
				return selectedVenue.location.coordinates[1] === pos.lat && selectedVenue.location.coordinates[0] === pos.lng;
			});

			if (selectedMarker) {
				selectedMarker.setIcon(
					leaflet.icon({
						iconUrl: selectedMark,
						iconSize: [43, 47],
						iconAnchor: [21.5, 47],
						popupAnchor: [1, -34],
					})
				);

				const pos = selectedMarker.getLatLng();
				const selectedBounds = leaflet.latLngBounds(
					leaflet.latLng(pos.lat - 0.01, pos.lng - 0.01),
					leaflet.latLng(pos.lat + 0.01, pos.lng + 0.01)
				);
				map.fitBounds(selectedBounds);
				map.panTo(pos);
			}
		}
		return clusterGroup;
	}
}

export const calculateGeoDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
	const r = 6371; // Radius of the earth in km
	const dLat = deg2rad(lat2 - lat1);
	const dLon = deg2rad(lon2 - lon1);
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return r * c; // Distance in km
};

export const deg2rad = (deg: number) => {
	return deg * (Math.PI / 180);
};

export const venueToAddress = (venue: Venue) => {
	const address = new Address();
	address.city = venue.city.de;
	address.lat = venue.location.coordinates[1];
	address.lng = venue.location.coordinates[0];
	address.street = venue.street;
	address.number = venue.number;
	address.postalCode = venue.postalCode;
	return address;
};

export const orderToAddress = (order: Order) => {
	const address = new Address();
	address.city = order.preorder.city;
	address.lat = order.preorder.lat;
	address.lng = order.preorder.lng;
	address.street = order.preorder.street;
	address.number = order.preorder.number;
	address.postalCode = order.preorder.postalCode;
	return address;
};
